import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import { FlowSession } from "../flow-sessions/types";
import {
  ArchivedCanvasListResponse,
  Canvas,
  CanvasCaptureJobResponse,
  CanvasListResponse,
  CanvasPublishResponse,
  CanvasResponse,
  CanvasSandboxProvisionResponse,
  CopyPublishedCanvasRequest,
  UpdateCanvasRequest,
} from "./types";

export * from "./heroImage";
export * from "./types";

const ENDPOINTS = {
  LIST: "/api/v2/org/:organization_id/user/:user_id/canvases",
  SHARED_WITH_ME:
    "/api/v2/org/:organization_id/user/:user_id/shared-with-me/canvases",
  GET: "/api/v2/org/:organization_id/user/:user_id/canvases/:canvas_id",
  UPDATE: "/api/v2/org/:organization_id/user/:user_id/canvases/:canvas_id",
  SET_FOLDER:
    "/api/v2/org/:organization_id/user/:user_id/canvases/:canvas_id/folder",
  DELETE: "/api/v2/org/:organization_id/user/:user_id/canvases/:canvas_id",
  RESTORE:
    "/api/v2/org/:organization_id/user/:user_id/canvases/:canvas_id/restore",
  ARCHIVED: "/api/v2/org/:organization_id/user/:user_id/archived/canvases",
  DUPLICATE:
    "/api/v2/org/:organization_id/user/:user_id/canvases/:canvas_id/duplicate",
  FLOW_SESSIONS:
    "/api/v2/org/:organization_id/user/:user_id/canvases/:canvas_id/flow-sessions",
  GENERATE_PREVIEW:
    "/api/v2/org/:organization_id/user/:user_id/canvases/:canvas_id/generate-preview",
  PUBLISH:
    "/api/v2/org/:organization_id/user/:user_id/canvases/:canvas_id/publish",
  UNPUBLISH:
    "/api/v2/org/:organization_id/user/:user_id/canvases/:canvas_id/unpublish",
  COPY_PUBLISHED:
    "/api/v2/org/:organization_id/user/:user_id/published-canvases/:canvas_id/copy",
  SANDBOX_PROVISION:
    "/api/v2/org/:organization_id/user/:user_id/canvases/:canvas_id/sandbox-provision",
  SANDBOX_TERMINAL_AGENT_CONTROL:
    "/api/v2/org/:organization_id/user/:user_id/canvases/:canvas_id/sandbox-terminal-agent-control",
};

export class CanvasAPI {
  constructor(private client: CohesiveAPIClient) {}

  /**
   * Lists canvases the user can access (workspace membership + direct shares),
   * ordered by `last_modified_at` descending. `limit` caps the result count
   * (server default 25, max 250).
   */
  async list(
    params?: { limit?: number },
    config?: AxiosRequestConfig,
  ): Promise<Canvas[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{ data: CanvasListResponse }>(
      route(ENDPOINTS.LIST, {
        organization_id: organizationId,
        user_id: userId,
      }),
      { ...config, params: { ...config?.params, ...params } },
    );
    return response.data.data.canvases;
  }

  async listSharedWithMe(config?: AxiosRequestConfig): Promise<Canvas[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{ data: CanvasListResponse }>(
      route(ENDPOINTS.SHARED_WITH_ME, {
        organization_id: organizationId,
        user_id: userId,
      }),
      config,
    );
    return response.data.data.canvases;
  }

  async getById(
    canvasId: string,
    config?: AxiosRequestConfig,
  ): Promise<Canvas> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{ data: CanvasResponse }>(
      route(ENDPOINTS.GET, {
        organization_id: organizationId,
        user_id: userId,
        canvas_id: canvasId,
      }),
      config,
    );
    return response.data.data.canvas;
  }

  async update(
    canvasId: string,
    data: UpdateCanvasRequest,
    config?: AxiosRequestConfig,
  ): Promise<Canvas> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.put<{ data: CanvasResponse }>(
      route(ENDPOINTS.UPDATE, {
        organization_id: organizationId,
        user_id: userId,
        canvas_id: canvasId,
      }),
      data,
      config,
    );
    return response.data.data.canvas;
  }

  /**
   * Move a canvas into a folder, or to the workspace root when `folderId` is null.
   * The destination folder must be a canvas folder in the canvas's own workspace.
   */
  async setFolder(
    canvasId: string,
    folderId: string | null,
    config?: AxiosRequestConfig,
  ): Promise<Canvas> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.put<{ data: CanvasResponse }>(
      route(ENDPOINTS.SET_FOLDER, {
        organization_id: organizationId,
        user_id: userId,
        canvas_id: canvasId,
      }),
      { folder_id: folderId },
      config,
    );
    return response.data.data.canvas;
  }

  async delete(canvasId: string, config?: AxiosRequestConfig): Promise<void> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    await this.client.axios.delete(
      route(ENDPOINTS.DELETE, {
        organization_id: organizationId,
        user_id: userId,
        canvas_id: canvasId,
      }),
      config,
    );
  }

  /** Restores a canvas from the archive; rejected if its parent workspace is archived. */
  async restore(
    canvasId: string,
    config?: AxiosRequestConfig,
  ): Promise<Canvas> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{ data: CanvasResponse }>(
      route(ENDPOINTS.RESTORE, {
        organization_id: organizationId,
        user_id: userId,
        canvas_id: canvasId,
      }),
      {},
      config,
    );
    return response.data.data.canvas;
  }

  /** Lists archived canvases, newest first (cursor-paginated by archived_at). */
  async listArchived(
    params?: { limit?: number; before_ts?: string },
    config?: AxiosRequestConfig,
  ): Promise<ArchivedCanvasListResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<ArchivedCanvasListResponse>(
      route(ENDPOINTS.ARCHIVED, {
        organization_id: organizationId,
        user_id: userId,
      }),
      { ...config, params: { ...config?.params, ...params } },
    );
    return response.data;
  }

  async duplicate(
    canvasId: string,
    config?: AxiosRequestConfig,
  ): Promise<Canvas> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{ data: CanvasResponse }>(
      route(ENDPOINTS.DUPLICATE, {
        organization_id: organizationId,
        user_id: userId,
        canvas_id: canvasId,
      }),
      {},
      config,
    );
    return response.data.data.canvas;
  }

  async getFlowSessions(
    canvasId: string,
    config?: AxiosRequestConfig,
  ): Promise<FlowSession[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<{
      data: { flow_sessions: FlowSession[] };
    }>(
      route(ENDPOINTS.FLOW_SESSIONS, {
        organization_id: organizationId,
        user_id: userId,
        canvas_id: canvasId,
      }),
      config,
    );
    return response.data.data.flow_sessions;
  }

  /**
   * Enqueue regeneration of the canvas's stored preview: name and description
   * from the LLM, plus the light/dark hero images captured server-side
   * (Temporal + headless Chromium). No client-side image capture.
   */
  async generatePreview(
    canvasId: string,
    options?: { appBaseUrl?: string },
    config?: AxiosRequestConfig,
  ): Promise<CanvasCaptureJobResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const body = options?.appBaseUrl
      ? { app_base_url: options.appBaseUrl }
      : undefined;

    const response = await this.client.axios.post<{
      data: CanvasCaptureJobResponse;
    }>(
      route(ENDPOINTS.GENERATE_PREVIEW, {
        organization_id: organizationId,
        user_id: userId,
        canvas_id: canvasId,
      }),
      body,
      config,
    );
    return response.data.data;
  }

  /**
   * Kick off an async public-canvas snapshot publish. Returns 202 with
   * `{ status: "publishing" }`; terminal status arrives via `canvas.updated`.
   * `appBaseUrl` is required (same contract as capture-screenshot) for OG capture.
   */
  async publish(
    canvasId: string,
    options: { appBaseUrl: string },
    config?: AxiosRequestConfig,
  ): Promise<CanvasPublishResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: CanvasPublishResponse;
    }>(
      route(ENDPOINTS.PUBLISH, {
        organization_id: organizationId,
        user_id: userId,
        canvas_id: canvasId,
      }),
      { app_base_url: options.appBaseUrl },
      config,
    );
    return response.data.data;
  }

  /**
   * Revoke the public canvas snapshot (sync). Clears publish columns and deletes
   * the S3 prefix / OG artifacts. Returns 204 with no body.
   */
  async unpublish(
    canvasId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    await this.client.axios.post(
      route(ENDPOINTS.UNPUBLISH, {
        organization_id: organizationId,
        user_id: userId,
        canvas_id: canvasId,
      }),
      undefined,
      config,
    );
  }

  /**
   * Copy a published canvas snapshot into one of the caller's workspaces
   * (omitted `workspace_id` = private workspace). `canvasId` is the SOURCE
   * (published) canvas id — it need not belong to the caller's org; the public
   * artifact is the sole input. Async: the server responds 202 with the new
   * canvas shell (`import_status: "importing"`) while file copies and state
   * hydration run — poll the canvas until `import_status` clears (done) or
   * turns `"failed"`.
   */
  async copyPublished(
    canvasId: string,
    data?: CopyPublishedCanvasRequest,
    config?: AxiosRequestConfig,
  ): Promise<Canvas> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{ data: CanvasResponse }>(
      route(ENDPOINTS.COPY_PUBLISHED, {
        organization_id: organizationId,
        user_id: userId,
        canvas_id: canvasId,
      }),
      data?.workspace_id ? { workspace_id: data.workspace_id } : {},
      config,
    );
    return response.data.data.canvas;
  }

  /**
   * Provision (or reuse) the canvas's one-per-(org, user, canvas) sandbox and mint a short-lived
   * canvas-scoped token for connecting to its terminal server. The returned url/token are identical
   * for every terminal widget on the canvas, so callers should dedupe rather than call per widget.
   */
  async sandboxProvision(
    canvasId: string,
    config?: AxiosRequestConfig,
  ): Promise<CanvasSandboxProvisionResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<{
      data: CanvasSandboxProvisionResponse;
    }>(
      route(ENDPOINTS.SANDBOX_PROVISION, {
        organization_id: organizationId,
        user_id: userId,
        canvas_id: canvasId,
      }),
      {},
      config,
    );
    return response.data.data;
  }

  /**
   * Grant or revoke the canvas agent's durable read/write access to a terminal widget's tmux
   * session (Tool_CanvasTerminal). The grant is canvas-scoped server state that persists across
   * messages until toggled off.
   */
  async setSandboxTerminalAgentControl(
    canvasId: string,
    options: { sessionId: string; enabled: boolean },
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    await this.client.axios.post(
      route(ENDPOINTS.SANDBOX_TERMINAL_AGENT_CONTROL, {
        organization_id: organizationId,
        user_id: userId,
        canvas_id: canvasId,
      }),
      { session_id: options.sessionId, enabled: options.enabled },
      config,
    );
  }
}
